import { useEffect, useState, useCallback, useRef } from 'react';
import { ConfirmModal } from './ConfirmModal';
import { useToast } from './ui/ToastProvider';
import { _registerDialogHost, type DialogHost } from '../lib/appDialog';

type Pending = {
  message: string; title: string; variant: 'danger' | 'warning' | 'info';
  confirmText: string; hideCancel: boolean; cancelText: string;
  resolve: (v: boolean) => void;
};

export default function AppDialogHost() {
  const { showSuccess, showError, showInfo, showWarning } = useToast();
  const [current, setCurrent] = useState<Pending | null>(null);
  const queue = useRef<Pending[]>([]);
  const pump = useCallback(() => setCurrent(queue.current.shift() || null), []);
  const enqueue = useCallback((p: Pending) => {
    queue.current.push(p);
    setCurrent(c => c ?? queue.current.shift() ?? null);
  }, []);

  useEffect(() => {
    const h: DialogHost = {
      alert: (message, opts) => new Promise<void>(res => enqueue({
        message, title: opts?.title ?? 'Avviso', variant: opts?.variant ?? 'info',
        confirmText: opts?.confirmText ?? 'OK', hideCancel: true, cancelText: '',
        resolve: () => res(),
      })),
      confirm: (message, opts) => new Promise<boolean>(res => enqueue({
        message, title: opts?.title ?? 'Conferma', variant: opts?.variant ?? 'warning',
        confirmText: opts?.confirmText ?? 'Conferma', hideCancel: false,
        cancelText: opts?.cancelText ?? 'Annulla', resolve: res,
      })),
      toast: (kind, message, title) => {
        const t = title;
        if (kind === 'success') showSuccess(t ?? 'Fatto', message);
        else if (kind === 'error') showError(t ?? 'Errore', message);
        else if (kind === 'warning') showWarning(t ?? 'Attenzione', message);
        else showInfo(t ?? 'Info', message);
      },
    };
    _registerDialogHost(h);
    return () => _registerDialogHost(null);
  }, [enqueue, showSuccess, showError, showInfo, showWarning]);

  if (!current) return null;
  return (
    <ConfirmModal
      isOpen={true}
      title={current.title}
      message={current.message}
      variant={current.variant}
      confirmText={current.confirmText}
      cancelText={current.cancelText}
      hideCancel={current.hideCancel}
      onConfirm={() => { const r = current.resolve; r(true); pump(); }}
      onClose={() => { const r = current.resolve; r(current.hideCancel ? true : false); pump(); }}
    />
  );
}
